/**
 * The resident-app clock — one source of truth for "what time is it" and
 * "how do we show it".
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * The home screen, the standby screen and every event time used to read the
 * tablet's OWN clock and format it in the tablet's OWN time zone. Two things go
 * wrong with that on a managed fleet:
 *
 *  1. The zone. A tablet that was provisioned in UTC, or reset to factory
 *     defaults, shows 13:00 while the corridor clock says 15:00. The instant is
 *     right, the wall-clock reading is off by whole hours. Every `toLocale*String`
 *     in this app therefore pins `timeZone` to Belgium — the operator is Belgian
 *     and every site is in Belgium — so the zone setting of the device is
 *     irrelevant to what the resident sees.
 *
 *  2. The clock itself. A device without automatic time (no NTP, or a dead RTC
 *     battery after a power cut) drifts by minutes. No time-zone setting can fix
 *     that. The connectivity probe already fetches the API health endpoint every
 *     60 s, and that response carries the server's timestamp; `syncClockFromServer`
 *     turns it into an offset that `now()` applies. The server is NTP-synced, so
 *     the app clock follows the server even when the device clock is wrong.
 *
 * Use `now()` instead of `new Date()` for anything the resident reads, and the
 * `formatClock*` helpers instead of bare `toLocale*String`. `Date.now()` stays
 * fine for measuring durations (latency, timeouts) — an offset is constant, so
 * it cancels out of every difference.
 */
import {
  DEFAULT_TIMEZONE,
  toLocalParts,
  fromLocalParts,
  type LocalParts,
} from '@caro/shared/activities';

/** The zone every resident-facing time is rendered in. */
export const CLOCK_TIME_ZONE = DEFAULT_TIMEZONE; // 'Europe/Brussels'

const OFFSET_KEY = 'caro_clock_offset_ms';

/**
 * A health round-trip slower than this says more about the network than about
 * the clock; the midpoint estimate would be off by up to half the RTT, so the
 * sample is dropped and the previous offset stays in force.
 */
const MAX_TRUSTED_RTT_MS = 3_000;

function readStoredOffset(): number {
  try {
    const raw = localStorage.getItem(OFFSET_KEY);
    const n = raw === null ? 0 : Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

let offsetMs = readStoredOffset();

/**
 * Fold a server timestamp into the clock offset.
 *
 * @param serverIso  the `timestamp` from GET /api/health
 * @param sentAt     `Date.now()` just before the request went out
 * @param receivedAt `Date.now()` when the response arrived
 * @returns the new skew in ms (server minus device), or null when the sample was
 *          unusable and nothing changed.
 */
export function syncClockFromServer(
  serverIso: unknown,
  sentAt: number,
  receivedAt: number,
): number | null {
  if (typeof serverIso !== 'string') return null;
  const serverMs = Date.parse(serverIso);
  if (!Number.isFinite(serverMs)) return null;
  const rtt = receivedAt - sentAt;
  if (!Number.isFinite(rtt) || rtt < 0 || rtt > MAX_TRUSTED_RTT_MS) return null;

  // The server stamped the response roughly halfway through the round-trip.
  const serverAtReceive = serverMs + rtt / 2;
  offsetMs = Math.round(serverAtReceive - receivedAt);
  try {
    localStorage.setItem(OFFSET_KEY, String(offsetMs));
  } catch {
    // storage unavailable — the in-memory offset still applies until reload
  }
  return offsetMs;
}

/** Server-minus-device clock skew currently applied, in ms. 0 = never synced / in sync. */
export function clockSkewMs(): number {
  return offsetMs;
}

/** Test seam: forget the offset (memory + storage). */
export function resetClockSync(): void {
  offsetMs = 0;
  try {
    localStorage.removeItem(OFFSET_KEY);
  } catch {
    // ignore
  }
}

/** The current instant, corrected by the server offset. */
export function now(): Date {
  return new Date(Date.now() + offsetMs);
}

/** `now().getTime()` without the allocation. */
export function nowMs(): number {
  return Date.now() + offsetMs;
}

/** The Belgian wall-clock reading of `date` (defaults to now). */
export function clockParts(date: Date = now()): LocalParts {
  return toLocalParts(date, CLOCK_TIME_ZONE);
}

/** The IANA zone the device itself is configured for, for telemetry. */
export function deviceTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/**
 * `hourCycle: 'h23'` rather than `hour12: false`: the latter rendered midnight
 * as "24:05" in a range of ICU builds, and when both are present `hour12` wins.
 */
const TIME_DEFAULTS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
};

/** HH:MM in Belgian time (override the parts via `opts`, the zone is fixed). */
export function formatClockTime(
  date: Date,
  locale: string,
  opts: Intl.DateTimeFormatOptions = {},
): string {
  return date.toLocaleTimeString(locale, { ...TIME_DEFAULTS, ...opts, timeZone: CLOCK_TIME_ZONE });
}

/** A date in Belgian time; `opts` are the usual `toLocaleDateString` parts. */
export function formatClockDate(
  date: Date,
  locale: string,
  opts: Intl.DateTimeFormatOptions = {},
): string {
  return date.toLocaleDateString(locale, { ...opts, timeZone: CLOCK_TIME_ZONE });
}

/** Date and time together, Belgian time, 24-hour clock. */
export function formatClockDateTime(
  date: Date,
  locale: string,
  opts: Intl.DateTimeFormatOptions = {},
): string {
  return date.toLocaleString(locale, { hourCycle: 'h23', ...opts, timeZone: CLOCK_TIME_ZONE });
}

/** 'YYYY-MM-DD' of `date` in Belgian time — the key for "same day?" questions. */
export function dayKey(date: Date = now()): string {
  const p = clockParts(date);
  const mm = String(p.month).padStart(2, '0');
  const dd = String(p.day).padStart(2, '0');
  return `${p.year}-${mm}-${dd}`;
}

/**
 * The instant at which Belgian midnight falls, `dayOffset` calendar days from
 * the day `date` belongs to. Replaces `d.setHours(0,0,0,0)`, which is midnight
 * in whatever zone the device happens to be in.
 */
export function startOfDay(date: Date = now(), dayOffset = 0): Date {
  const p = clockParts(date);
  // Let Date.UTC carry the day overflow (day 32 → next month) for us.
  const shifted = new Date(Date.UTC(p.year, p.month - 1, p.day + dayOffset));
  return fromLocalParts(
    {
      year: shifted.getUTCFullYear(),
      month: shifted.getUTCMonth() + 1,
      day: shifted.getUTCDate(),
      hour: 0,
      minute: 0,
      second: 0,
    },
    CLOCK_TIME_ZONE,
  );
}
